import React, { useEffect, useState } from 'react';
import api, { suppliersAPI, sitesAPI, openAuthPdf } from '@/lib/api';
import PrintButton from '@/components/PrintButton';
import { formatCurrency, matchesSearch } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Truck, Plus, Pencil, Trash2, Search, Mail, Phone, MapPin, Eye, EyeOff, FileText, Download, X, ClipboardList, LayoutGrid, List, User, CheckSquare, Square, Users, FlaskConical, Save, MessageCircle, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import InvoiceCalibrationDialog from '@/components/InvoiceCalibrationDialog';
import InvoiceAuditDialog from '@/components/InvoiceAuditDialog';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export default function SuppliersPage() {
    const { user } = useAuth();
    const canEditCompanies = user && (user.role === 'admin' || user.role === 'supervisor');
    const canEditSuppliers = user && (user.role === 'admin' || user.role === 'supervisor' || (user.permissions || []).includes('suppliers_edit'));
    const isAdminUser = user?.role === 'admin';
    const canCalibrate = user && (user.role === 'admin' || user.role === 'supervisor' || ['orders_import_invoice', 'receptions_create'].some(p => (user.permissions || []).includes(p)));
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showContacts, setShowContacts] = useState(true);
    const [viewMode, setViewMode] = useState('list'); // 'cards' or 'list' - default to list
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [supplierToDelete, setSupplierToDelete] = useState(null);
    
    const [form, setForm] = useState({
        name: '', 
        email: '', 
        phone: '', 
        address: '',
        payment_mode: 'comptant',
        contacts: [],  // [{title: "Commercial", name: "...", phone: "...", email: "...", send_orders: true, assigned_sites: []}, ...]
        fuji_express: true,
        concept_lab: true,
        my_food: true,
        boucherie: true,
    });
    
    // Mercuriale Dialog
    const [mercurialeDialog, setMercurialeDialog] = useState(false);
    // Calibration format facture
    const [calibrationDialog, setCalibrationDialog] = useState(false);
    const [auditDialog, setAuditDialog] = useState(false);
    const [calibrationSupplier, setCalibrationSupplier] = useState(null);
    const [profilesSummary, setProfilesSummary] = useState({});
    const [chainPickerOpen, setChainPickerOpen] = useState(false);
    const [chainMode, setChainMode] = useState(false);
    const [chainSearch, setChainSearch] = useState('');
    const [mercurialeSupplier, setMercurialeSupplier] = useState(null);
    const [mercurialeProducts, setMercurialeProducts] = useState([]);
    const [mercurialeLoading, setMercurialeLoading] = useState(false);

    // Email Test Mode (also covers WhatsApp test redirect via test_phone)
    const [testMode, setTestMode] = useState({ enabled: false, test_email: '', test_phone: '' });
    const [testModeLoading, setTestModeLoading] = useState(false);

    // Sites list for contact assignment
    const [allSites, setAllSites] = useState([]);

    useEffect(() => {
        loadSuppliers();
        if (user?.role === 'admin') loadTestMode();
        loadSites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSites = async () => {
        try {
            const response = await sitesAPI.getAll();
            const sites = response.data || [];
            // Tri personnalisé des sites Fuji Express
            const order = ['génipa', 'genipa', 'cluny', 'dillon', 'océanis', 'oceanis', 'vauclin', 'siège', 'siege', 'concept', 'my food'];
            sites.sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                const idxA = order.findIndex(k => nameA.includes(k));
                const idxB = order.findIndex(k => nameB.includes(k));
                const posA = idxA >= 0 ? idxA : 999;
                const posB = idxB >= 0 ? idxB : 999;
                return posA - posB;
            });
            setAllSites(sites);
        } catch (error) {
            console.error('Erreur chargement sites:', error);
        }
    };

    const loadTestMode = async () => {
        try {
            const response = await suppliersAPI.getEmailTestMode();
            setTestMode(response.data);
        } catch (error) {
            console.error('Erreur chargement mode test:', error);
        }
    };

    const handleToggleTestMode = async (enabled) => {
        const newState = { ...testMode, enabled };
        setTestMode(newState);
        setTestModeLoading(true);
        try {
            await suppliersAPI.setEmailTestMode(newState);
            toast.success(enabled ? 'Mode test activé' : 'Mode test désactivé');
        } catch (error) {
            toast.error('Erreur lors de la mise à jour du mode test');
            setTestMode(prev => ({ ...prev, enabled: !enabled }));
        } finally {
            setTestModeLoading(false);
        }
    };

    const handleSaveTestEmail = async () => {
        if (testMode.enabled && !testMode.test_email && !testMode.test_phone) {
            toast.error('Veuillez saisir au moins un email ou numéro de test');
            return;
        }
        setTestModeLoading(true);
        try {
            await suppliersAPI.setEmailTestMode(testMode);
            toast.success('Paramètres du mode test enregistrés');
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setTestModeLoading(false);
        }
    };

    const loadSuppliers = async () => {
        try {
            const response = await suppliersAPI.getAll();
            setSuppliers(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des fournisseurs');
        } finally {
            setLoading(false);
        }
    };

    const loadProfilesSummary = async () => {
        try {
            const res = await api.get('/suppliers/invoice-profiles/summary');
            setProfilesSummary(res.data || {});
        } catch { /* silencieux */ }
    };
    useEffect(() => { if (canCalibrate) loadProfilesSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const uncalibrated = canCalibrate ? suppliers.filter(s => !profilesSummary[s.id]?.calibrated_at) : [];

    const chainList = [...uncalibrated]
        .filter(s => !chainSearch || matchesSearch(s.name || '', chainSearch))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));

    const pickChainSupplier = (supplier) => {
        setChainPickerOpen(false);
        setChainMode(true);
        setCalibrationSupplier(supplier);
        setCalibrationDialog(true);
    };

    const onChainCalibrationDone = () => {
        loadProfilesSummary();
        setCalibrationDialog(false);
        setChainPickerOpen(true); // retour à la liste pour choisir le suivant
    };

    const filteredSuppliers = suppliers.filter(s =>
        matchesSearch(s.name, searchTerm) ||
        matchesSearch(s.email, searchTerm)
    ).sort((a, b) => a.name.localeCompare(b.name, 'fr'));

    const handleOpenDialog = (supplier = null) => {
        if (supplier) {
            setEditingSupplier(supplier);
            setForm({
                name: supplier.name,
                email: supplier.email || '',
                phone: supplier.phone || '',
                address: supplier.address || '',
                payment_mode: supplier.payment_mode || 'comptant',
                contacts: supplier.contacts || [],
                fuji_express: supplier.fuji_express !== false,
                concept_lab: supplier.concept_lab !== false,
                my_food: supplier.my_food !== false,
                boucherie: supplier.boucherie !== false,
            });
        } else {
            setEditingSupplier(null);
            setForm({ 
                name: '', 
                email: '', 
                phone: '', 
                address: '',
                payment_mode: 'comptant',
                contacts: [],
                fuji_express: true,
                concept_lab: true,
                my_food: true,
                boucherie: true,
            });
        }
        setDialogOpen(true);
    };
    
    // Fonctions pour gérer les contacts
    const handleAddContact = () => {
        setForm({
            ...form,
            contacts: [...form.contacts, { title: '', name: '', phone: '', email: '', send_orders: false, send_whatsapp: false, assigned_sites: [] }]
        });
    };
    
    const handleRemoveContact = (index) => {
        setForm({
            ...form,
            contacts: form.contacts.filter((_, i) => i !== index)
        });
    };
    
    const handleUpdateContact = (index, field, value) => {
        const newContacts = [...form.contacts];
        newContacts[index] = { ...newContacts[index], [field]: value };
        setForm({ ...form, contacts: newContacts });
    };
    
    const handleToggleSendOrders = (index) => {
        const newContacts = [...form.contacts];
        newContacts[index] = { ...newContacts[index], send_orders: !newContacts[index].send_orders };
        setForm({ ...form, contacts: newContacts });
    };

    // Mark/unmark a contact as eligible for WhatsApp sharing. Only contacts
    // with send_whatsapp=true will appear in the order's WhatsApp dialog.
    const handleToggleSendWhatsApp = (index) => {
        const newContacts = [...form.contacts];
        newContacts[index] = { ...newContacts[index], send_whatsapp: !newContacts[index].send_whatsapp };
        setForm({ ...form, contacts: newContacts });
    };

    const handleSave = async () => {
        if (!form.name) {
            toast.error('Le nom est requis');
            return;
        }

        try {
            if (editingSupplier) {
                await suppliersAPI.update(editingSupplier.id, form);
                toast.success('Fournisseur mis à jour');
            } else {
                await suppliersAPI.create(form);
                toast.success('Fournisseur créé');
            }
            setDialogOpen(false);
            loadSuppliers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    const handleDeleteClick = (supplier) => {
        setSupplierToDelete(supplier);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await suppliersAPI.delete(supplierToDelete.id);
            toast.success('Fournisseur supprimé');
            setDeleteDialog(false);
            loadSuppliers();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleOpenMercuriale = async (supplier) => {
        setMercurialeSupplier(supplier);
        setMercurialeDialog(true);
        setMercurialeLoading(true);
        try {
            const response = await suppliersAPI.getProducts(supplier.id);
            setMercurialeProducts(response.data.products);
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
            setMercurialeProducts([]);
        } finally {
            setMercurialeLoading(false);
        }
    };

    const handleDownloadMercurialePdf = () => {
        if (mercurialeSupplier) {
            openAuthPdf(suppliersAPI.getMercurialePdfUrl(mercurialeSupplier.id), 'Erreur lors de la génération de la mercuriale');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="suppliers-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Fournisseurs</h1>
                    <p className="text-slate-500 mt-1">Gestion de vos fournisseurs</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {uncalibrated.length > 0 && (
                        <Button
                            variant="outline"
                            className="border-orange-300 text-orange-600 hover:bg-orange-50"
                            onClick={() => setChainPickerOpen(true)}
                            data-testid="chain-calibration-btn"
                        >
                            <ScanLine className="w-4 h-4 mr-2" />
                            Calibration à la chaîne ({uncalibrated.length} à faire)
                        </Button>
                    )}
                    {canCalibrate && (
                    <Button variant="outline" onClick={() => setAuditDialog(true)} data-testid="invoice-audit-btn">
                        <ScanLine className="w-4 h-4 mr-2 text-orange-500" />
                        Contrôle IA
                    </Button>
                    )}
                    {canEditSuppliers && (
                    <Button onClick={() => handleOpenDialog()} className="btn-primary" data-testid="add-supplier-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau fournisseur
                    </Button>
                    )}
                </div>
            </div>

            {/* Email Test Mode Banner (admin uniquement — le réglage est protégé côté serveur) */}
            {isAdminUser && (
            <Card className={`border-2 transition-colors ${testMode.enabled ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`} data-testid="test-mode-card">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <FlaskConical className={`w-5 h-5 ${testMode.enabled ? 'text-amber-600' : 'text-slate-400'}`} />
                            <div>
                                <span className="font-medium text-sm text-slate-700">Mode Test (Email + WhatsApp)</span>
                                {testMode.enabled && (
                                    <span className="ml-2 text-xs font-semibold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">ACTIF</span>
                                )}
                            </div>
                            <Switch
                                checked={testMode.enabled}
                                onCheckedChange={handleToggleTestMode}
                                disabled={testModeLoading}
                                data-testid="test-mode-toggle"
                            />
                        </div>
                        {testMode.enabled && (
                            <div className="flex items-center gap-2 flex-1 flex-wrap sm:flex-nowrap">
                                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                                    <Mail className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                    <Input
                                        type="email"
                                        value={testMode.test_email}
                                        onChange={(e) => setTestMode(prev => ({ ...prev, test_email: e.target.value }))}
                                        placeholder="adresse@test.com"
                                        className="flex-1 bg-white border-amber-300 focus:border-amber-500"
                                        data-testid="test-email-input"
                                    />
                                </div>
                                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                                    <MessageCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                    <Input
                                        type="tel"
                                        value={testMode.test_phone || ''}
                                        onChange={(e) => setTestMode(prev => ({ ...prev, test_phone: e.target.value }))}
                                        placeholder="+596 6XX XX XX XX"
                                        className="flex-1 bg-white border-amber-300 focus:border-amber-500"
                                        data-testid="test-phone-input"
                                    />
                                </div>
                                <Button
                                    size="sm"
                                    onClick={handleSaveTestEmail}
                                    disabled={testModeLoading}
                                    className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
                                    data-testid="save-test-email-btn"
                                >
                                    <Save className="w-4 h-4 mr-1" />
                                    Enregistrer
                                </Button>
                            </div>
                        )}
                        {!testMode.enabled && (
                            <p className="text-xs text-slate-500">
                                Activez ce mode pour rediriger les emails et partages WhatsApp de commande vers une adresse / un numéro de test.
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
            )}

            {/* Search and Display Options */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Rechercher par nom ou email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-suppliers-input"
                            />
                        </div>
                        
                        {/* View mode toggle */}
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('cards')}
                                className={`p-2 rounded ${viewMode === 'cards' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                                title="Vue en cartes"
                            >
                                <LayoutGrid className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                                title="Vue en liste"
                            >
                                <List className="w-4 h-4" />
                            </button>
                        </div>
                        
                        {viewMode === 'cards' && (
                            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    {showContacts ? (
                                        <Eye className="w-4 h-4" />
                                    ) : (
                                        <EyeOff className="w-4 h-4" />
                                    )}
                                    <span>Afficher les coordonnées</span>
                                </div>
                                <Switch
                                    checked={showContacts}
                                    onCheckedChange={setShowContacts}
                                    data-testid="toggle-contacts-switch"
                                />
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Suppliers Grid/List */}
            {filteredSuppliers.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Truck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">Aucun fournisseur trouvé</p>
                    </CardContent>
                </Card>
            ) : viewMode === 'list' ? (
                /* Vue liste compacte */
                <Card>
                    <CardContent className="p-0">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b bg-slate-50">
                                    <th className="text-left p-3 font-medium text-slate-700">Fournisseur</th>
                                    <th className="text-center p-3 font-medium text-slate-700 w-32">Mercuriale</th>
                                    <th className="text-center p-3 font-medium text-slate-700 w-24">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSuppliers.map((supplier, index) => (
                                    <tr 
                                        key={supplier.id} 
                                        className={`border-b last:border-0 hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-25'}`}
                                        data-testid={`supplier-row-${supplier.id}`}
                                    >
                                        <td className="p-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <Truck className="w-4 h-4 text-emerald-600" />
                                                </div>
                                                <span className="font-medium text-slate-900">{supplier.name}</span>
                                            </div>
                                        </td>
                                        <td className="p-3 text-center">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                                onClick={() => handleOpenMercuriale(supplier)}
                                                data-testid={`mercuriale-list-${supplier.id}`}
                                            >
                                                <FileText className="w-4 h-4" />
                                            </Button>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex items-center justify-center gap-1">
                                                {canCalibrate && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={profilesSummary[supplier.id]?.calibrated_at ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'}
                                                    title={profilesSummary[supplier.id]?.calibrated_at ? `Format calibré le ${new Date(profilesSummary[supplier.id].calibrated_at).toLocaleDateString('fr-FR')}` : 'Calibrer le format de facture (IA)'}
                                                    onClick={() => { setCalibrationSupplier(supplier); setCalibrationDialog(true); }}
                                                    data-testid={`calibrate-supplier-${supplier.id}`}
                                                >
                                                    <ScanLine className="w-4 h-4" />
                                                </Button>
                                                )}
                                                {canEditSuppliers && (<>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenDialog(supplier)}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                    onClick={() => handleDeleteClick(supplier)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                                </>)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            ) : (
                /* Vue cartes */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSuppliers.map((supplier) => (
                        <Card key={supplier.id} className="card-interactive" data-testid={`supplier-card-${supplier.id}`}>
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                                        <Truck className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <div className="flex gap-1">
                                        {canCalibrate && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className={profilesSummary[supplier.id]?.calibrated_at ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'}
                                            title={profilesSummary[supplier.id]?.calibrated_at ? `Format calibré le ${new Date(profilesSummary[supplier.id].calibrated_at).toLocaleDateString('fr-FR')}` : 'Calibrer le format de facture (IA)'}
                                            onClick={() => { setCalibrationSupplier(supplier); setCalibrationDialog(true); }}
                                            data-testid={`calibrate-supplier-card-${supplier.id}`}
                                        >
                                            <ScanLine className="w-4 h-4" />
                                        </Button>
                                        )}
                                        {canEditSuppliers && (<>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleOpenDialog(supplier)}
                                            data-testid={`edit-supplier-${supplier.id}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleDeleteClick(supplier)}
                                            data-testid={`delete-supplier-${supplier.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                        </>)}
                                    </div>
                                </div>
                                <h3 className="font-semibold text-lg text-slate-900 mb-3">{supplier.name}</h3>
                                
                                {/* Mercuriale Button */}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full mb-3 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                    onClick={() => handleOpenMercuriale(supplier)}
                                    data-testid={`mercuriale-supplier-${supplier.id}`}
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Mercuriale
                                </Button>
                                
                                {showContacts && (
                                    <div className="space-y-3 text-sm">
                                        {/* Infos générales */}
                                        <div className="space-y-1 text-slate-500">
                                            {supplier.email && (
                                                <div className="flex items-center gap-2">
                                                    <Mail className="w-4 h-4" />
                                                    <span className="truncate">{supplier.email}</span>
                                                </div>
                                            )}
                                            {supplier.phone && (
                                                <div className="flex items-center gap-2">
                                                    <Phone className="w-4 h-4" />
                                                    <span>{supplier.phone}</span>
                                                </div>
                                            )}
                                            {supplier.address && (
                                                <div className="flex items-start gap-2">
                                                    <MapPin className="w-4 h-4 mt-0.5" />
                                                    <span className="line-clamp-2">{supplier.address}</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Bloc Contacts */}
                                        {supplier.contacts && supplier.contacts.length > 0 && (
                                            <div className="pt-2 border-t">
                                                <div className="text-xs font-medium text-slate-400 mb-1 flex items-center gap-1">
                                                    <Users className="w-3 h-3" /> Contacts
                                                </div>
                                                <div className="space-y-2">
                                                    {supplier.contacts.map((contact, idx) => (
                                                        <div key={idx} className="text-slate-600 text-xs">
                                                            <div className="flex items-center gap-2">
                                                                {contact.title && (
                                                                    <span className="font-semibold text-slate-500">{contact.title}</span>
                                                                )}
                                                                {contact.name && (
                                                                    <span className="font-medium">{contact.name}</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-3 text-slate-500 mt-0.5">
                                                                {contact.phone && (
                                                                    <span className="flex items-center gap-1">
                                                                        <Phone className="w-3 h-3" />
                                                                        {contact.phone}
                                                                        {contact.send_whatsapp && (
                                                                            <MessageCircle className="w-3 h-3 text-emerald-500" title="Partage WhatsApp activé" />
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {contact.email && (
                                                                    <span className="flex items-center gap-1">
                                                                        <Mail className="w-3 h-3" />
                                                                        {contact.email}
                                                                        {contact.send_orders && (
                                                                            <CheckSquare className="w-3 h-3 text-emerald-500" />
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {contact.send_orders && contact.assigned_sites && contact.assigned_sites.length > 0 && (
                                                                <div className="flex items-center gap-1 mt-0.5 ml-0.5">
                                                                    <MapPin className="w-3 h-3 text-slate-400" />
                                                                    {contact.assigned_sites.map(siteId => {
                                                                        const site = allSites.find(s => s.id === siteId);
                                                                        return site ? (
                                                                            <span key={siteId} className="text-[10px] px-1.5 py-0 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
                                                                                {site.name}
                                                                            </span>
                                                                        ) : null;
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {!supplier.email && !supplier.phone && !supplier.address && 
                                         (!supplier.contacts || supplier.contacts.length === 0) && (
                                            <p className="text-slate-400 italic">Aucune coordonnée renseignée</p>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingSupplier ? 'Modifiez les informations du fournisseur' : 'Ajoutez un nouveau fournisseur'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                        {/* Informations générales */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-slate-700 border-b pb-2">Informations générales</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Nom *</Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Nom du fournisseur"
                                        data-testid="supplier-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email général</Label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="contact@fournisseur.com"
                                        data-testid="supplier-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Téléphone</Label>
                                    <Input
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        placeholder="+33 1 23 45 67 89"
                                        data-testid="supplier-phone-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Adresse</Label>
                                    <Input
                                        value={form.address}
                                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                                        placeholder="Adresse complète"
                                        data-testid="supplier-address-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Mode de règlement</Label>
                                    <Select
                                        value={form.payment_mode || 'comptant'}
                                        onValueChange={(v) => setForm({ ...form, payment_mode: v })}
                                    >
                                        <SelectTrigger data-testid="supplier-payment-mode-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="comptant">Comptant</SelectItem>
                                            <SelectItem value="en_compte">En compte</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        
                        {/* Bloc Utilisable par (visible admin/supervisor uniquement) */}
                        {canEditCompanies && (
                            <div className="space-y-2 border-t pt-4">
                                <h3 className="font-semibold text-slate-700 text-sm">
                                    Utilisable par
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Entreprises pour lesquelles ce fournisseur est disponible dans les commandes et réceptions.
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {[
                                        { key: 'fuji_express', label: 'Fuji Express' },
                                        { key: 'concept_lab', label: 'Concept Lab' },
                                        { key: 'my_food', label: 'My Food' },
                                        { key: 'boucherie', label: 'Boucherie' },
                                    ].map(({ key, label }) => (
                                        <label
                                            key={key}
                                            className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm"
                                            data-testid={`supplier-company-${key}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={!!form[key]}
                                                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                                                className="rounded"
                                            />
                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Bloc Contacts */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between border-b pb-2">
                                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Contacts
                                </h3>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddContact}
                                    data-testid="add-contact-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter un contact
                                </Button>
                            </div>
                            
                            <p className="text-xs text-slate-500">
                                Cochez l&apos;icône email pour envoyer les bons de commande à ce contact,
                                ou l&apos;icône WhatsApp pour autoriser le partage de commande par WhatsApp à son numéro.
                            </p>
                            
                            {form.contacts.length === 0 ? (
                                <div className="text-sm text-slate-400 italic py-4 text-center border border-dashed rounded-lg">
                                    Aucun contact. Cliquez sur "Ajouter un contact" pour en créer un.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {form.contacts.map((contact, index) => (
                                        <div key={index} className="p-3 bg-slate-50 rounded-lg border">
                                            <div className="flex flex-col gap-2">
                                                {/* Ligne 1 : Titre + Nom + Bouton supprimer */}
                                                <div className="flex items-start gap-2">
                                                    <div className="w-28 flex-shrink-0">
                                                        <Input
                                                            value={contact.title}
                                                            onChange={(e) => handleUpdateContact(index, 'title', e.target.value)}
                                                            placeholder="Titre"
                                                            className="h-8 text-sm font-medium"
                                                            data-testid={`contact-title-${index}`}
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <Input
                                                            value={contact.name}
                                                            onChange={(e) => handleUpdateContact(index, 'name', e.target.value)}
                                                            placeholder="Nom"
                                                            className="h-8 text-sm"
                                                            data-testid={`contact-name-${index}`}
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveContact(index)}
                                                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0 flex-shrink-0"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                                {/* Ligne 2 : Téléphone + Email + Toggle envoi */}
                                                <div className="flex items-center gap-2">
                                                    <div className="w-44 flex-shrink-0 flex items-center gap-1">
                                                        <Input
                                                            value={contact.phone}
                                                            onChange={(e) => handleUpdateContact(index, 'phone', e.target.value)}
                                                            placeholder="Téléphone"
                                                            className="h-8 text-sm"
                                                            data-testid={`contact-phone-${index}`}
                                                        />
                                                        {/* WhatsApp eligibility: only contacts with this flag
                                                            appear in the order's WhatsApp share dialog. The
                                                            phone field is reused (no separate wa number). */}
                                                        <div
                                                            className="flex items-center gap-1 flex-shrink-0"
                                                            title="Autoriser le partage de commande par WhatsApp à ce numéro"
                                                        >
                                                            <Checkbox
                                                                checked={!!contact.send_whatsapp}
                                                                onCheckedChange={() => handleToggleSendWhatsApp(index)}
                                                                data-testid={`contact-send-whatsapp-${index}`}
                                                            />
                                                            <MessageCircle className={`w-4 h-4 ${contact.send_whatsapp ? 'text-emerald-500' : 'text-slate-300'}`} />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 min-w-0 flex items-center gap-2">
                                                        <Input
                                                            type="email"
                                                            value={contact.email}
                                                            onChange={(e) => handleUpdateContact(index, 'email', e.target.value)}
                                                            placeholder="Email"
                                                            className="h-8 text-sm flex-1 min-w-0"
                                                            data-testid={`contact-email-${index}`}
                                                        />
                                                        <div className="flex items-center gap-1 flex-shrink-0" title="Envoyer les commandes à cet email">
                                                            <Checkbox
                                                                checked={contact.send_orders}
                                                                onCheckedChange={() => handleToggleSendOrders(index)}
                                                                data-testid={`contact-send-orders-${index}`}
                                                            />
                                                            <Mail className={`w-4 h-4 ${contact.send_orders ? 'text-emerald-500' : 'text-slate-300'}`} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Sites assignés - visible quand send_orders est coché */}
                                            {contact.send_orders && allSites.length > 0 && (
                                                <div className="mt-2 pt-2 border-t border-slate-200">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                        <span className="text-xs text-slate-500 flex-shrink-0">Sites :</span>
                                                        {allSites.map(site => {
                                                            const assigned = (contact.assigned_sites || []).includes(site.id);
                                                            return (
                                                                <button
                                                                    key={site.id}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const current = contact.assigned_sites || [];
                                                                        const updated = assigned
                                                                            ? current.filter(s => s !== site.id)
                                                                            : [...current, site.id];
                                                                        handleUpdateContact(index, 'assigned_sites', updated);
                                                                    }}
                                                                    className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                                                        assigned
                                                                            ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                                                                            : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                                                                    }`}
                                                                    data-testid={`contact-site-${index}-${site.id}`}
                                                                >
                                                                    {site.name}
                                                                </button>
                                                            );
                                                        })}
                                                        {(!contact.assigned_sites || contact.assigned_sites.length === 0) && (
                                                            <span className="text-[10px] text-slate-400 italic">tous les sites</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Légende */}
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Checkbox checked disabled className="h-3 w-3" />
                                <Mail className="w-3 h-3 text-emerald-500" />
                                <span>= recevra les bons de commande par email</span>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave} className="btn-primary" data-testid="save-supplier-btn">
                            {editingSupplier ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer ce fournisseur ? Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Mercuriale Dialog */}
            <Dialog open={mercurialeDialog} onOpenChange={setMercurialeDialog}>
                <DialogContent className="sm:max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-emerald-600" />
                            Mercuriale - {mercurialeSupplier?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Liste des produits et tarifs de ce fournisseur
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto py-4">
                        {mercurialeLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                            </div>
                        ) : mercurialeProducts.length === 0 ? (
                            <div className="text-center py-12">
                                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                <p className="text-slate-500">Aucun produit référencé pour ce fournisseur</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-500">
                                    <span className="font-medium text-slate-700">{mercurialeProducts.length}</span> produit(s) référencé(s)
                                </p>
                                
                                {/* Group by category */}
                                {(() => {
                                    const grouped = mercurialeProducts.reduce((acc, product) => {
                                        const cat = product.category || 'Sans catégorie';
                                        if (!acc[cat]) acc[cat] = [];
                                        acc[cat].push(product);
                                        return acc;
                                    }, {});
                                    
                                    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, products]) => (
                                        <div key={category} className="border rounded-lg overflow-x-auto">
                                            <div className="bg-slate-100 px-4 py-2 font-medium text-slate-700">
                                                {category}
                                            </div>
                                            <table className="w-full text-sm min-w-[900px]">
                                                <thead className="bg-slate-50">
                                                    <tr>
                                                        <th className="text-left px-3 py-2 font-medium text-slate-600">Référence</th>
                                                        <th className="text-left px-3 py-2 font-medium text-slate-600">Désignation</th>
                                                        <th className="text-left px-3 py-2 font-medium text-slate-600">Colisage</th>
                                                        <th className="text-left px-3 py-2 font-medium text-slate-600">Con. vente</th>
                                                        <th className="text-left px-3 py-2 font-medium text-slate-600">Unité Fact.</th>
                                                        <th className="text-left px-3 py-2 font-medium text-slate-600">Contenance</th>
                                                        <th className="text-right px-3 py-2 font-medium text-slate-600">Prix Unitaire</th>
                                                        <th className="text-right px-3 py-2 font-medium text-slate-600">Tarif u.cont</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {products.map((product) => {
                                                        // Format colisage
                                                        const orderUnit = product.order_unit || '';
                                                        const packagingQty = product.packaging_qty || 1;
                                                        const packagingQtyInt = Math.floor(packagingQty); // Remove decimals
                                                        const billingUnit = product.billing_unit || '';
                                                        const unknownPackaging = product.unknown_packaging || false;
                                                        
                                                        let colisage = '-';
                                                        if (unknownPackaging) {
                                                            // Colisage inconnu: afficher "-"
                                                            colisage = '-';
                                                        } else if (orderUnit && packagingQty > 1 && billingUnit) {
                                                            colisage = `${orderUnit} de ${packagingQtyInt} ${billingUnit}`;
                                                        } else if (orderUnit && packagingQty > 1) {
                                                            colisage = `${orderUnit} de ${packagingQtyInt}`;
                                                        } else if (orderUnit) {
                                                            colisage = orderUnit;
                                                        }
                                                        
                                                        // Format contenance (par unité de colisage)
                                                        const capacityValue = product.capacity_value;
                                                        const capacityUnit = product.capacity_unit || '';
                                                        const contenance = capacityValue && capacityUnit 
                                                            ? `${capacityValue} ${capacityUnit}`
                                                            : '-';
                                                        
                                                        // Con. vente = conditionnement de vente réel
                                                        // Si sell_by_detail=true: unité de facturation (ex: Sachet)
                                                        // Si sell_by_detail=false: colisage complet (ex: carton de 10 Sachet)
                                                        const sellByDetail = product.sell_by_detail || false;
                                                        let conVente = '-';
                                                        if (sellByDetail) {
                                                            // Vente au détail: unité = billing_unit
                                                            conVente = billingUnit || '-';
                                                        } else if (orderUnit && packagingQty > 1 && billingUnit) {
                                                            // Vente par colisage complet
                                                            conVente = `${orderUnit} de ${packagingQtyInt} ${billingUnit}`;
                                                        } else if (orderUnit && packagingQty > 1) {
                                                            conVente = `${orderUnit} de ${packagingQtyInt}`;
                                                        } else if (orderUnit) {
                                                            conVente = orderUnit;
                                                        } else if (billingUnit) {
                                                            conVente = billingUnit;
                                                        }
                                                        
                                                        // Tarif par unité de contenance
                                                        let tarifUcont = '-';
                                                        if (capacityValue && capacityValue > 0) {
                                                            const tarifPerUnit = product.unit_price / capacityValue;
                                                            tarifUcont = `${tarifPerUnit.toFixed(2)} €/${capacityUnit}`;
                                                        }
                                                        
                                                        return (
                                                            <tr key={product.id} className="border-t border-slate-100 hover:bg-slate-50">
                                                                <td className="px-3 py-2 text-slate-600 font-mono text-xs">
                                                                    {product.supplier_ref || '-'}
                                                                </td>
                                                                <td className="px-3 py-2 font-medium text-slate-900">
                                                                    {product.supplier_name_product || product.industrial_product_name}
                                                                </td>
                                                                <td className="px-3 py-2 text-slate-600">
                                                                    {colisage}
                                                                </td>
                                                                <td className="px-3 py-2 text-slate-600">
                                                                    {conVente}
                                                                </td>
                                                                <td className="px-3 py-2 text-slate-600">
                                                                    {product.invoice_unit || billingUnit || '-'}
                                                                </td>
                                                                <td className="px-3 py-2 text-slate-600">
                                                                    {contenance}
                                                                </td>
                                                                <td className="px-3 py-2 text-right font-semibold text-slate-900">
                                                                    {formatCurrency(product.unit_price)}
                                                                </td>
                                                                <td className="px-3 py-2 text-right text-emerald-600 font-medium">
                                                                    {tarifUcont}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter className="border-t pt-4">
                        <Button variant="outline" onClick={() => setMercurialeDialog(false)}>
                            Fermer
                        </Button>
                        <Button 
                            onClick={handleDownloadMercurialePdf}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            disabled={mercurialeProducts.length === 0}
                            data-testid="download-mercuriale-pdf"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger PDF
                        </Button>
                        <PrintButton size="default" testId="print-mercuriale-pdf"
                            disabled={mercurialeProducts.length === 0 || !mercurialeSupplier}
                            url={() => suppliersAPI.getMercurialePdfUrl(mercurialeSupplier.id)} />
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <InvoiceAuditDialog open={auditDialog} onClose={() => setAuditDialog(false)} />
            <InvoiceCalibrationDialog
                open={calibrationDialog}
                onClose={() => { setCalibrationDialog(false); loadProfilesSummary(); if (chainMode) setChainPickerOpen(true); setChainMode(false); }}
                supplier={calibrationSupplier}
                sites={allSites}
                chain={chainMode ? { remaining: uncalibrated.length } : null}
                onSaved={chainMode ? onChainCalibrationDone : null}
            />

            <Dialog open={chainPickerOpen} onOpenChange={setChainPickerOpen}>
                <DialogContent className="sm:max-w-md" data-testid="chain-picker-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ScanLine className="w-5 h-5 text-orange-500" /> Calibration à la chaîne
                        </DialogTitle>
                        <DialogDescription>
                            {uncalibrated.length} fournisseur(s) restant(s) à calibrer — choisissez par lequel commencer.
                            Après chaque mémorisation, vous reviendrez à cette liste.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        placeholder="Rechercher un fournisseur…"
                        value={chainSearch}
                        onChange={(e) => setChainSearch(e.target.value)}
                        className="h-9"
                        data-testid="chain-picker-search"
                    />
                    <div className="max-h-80 overflow-y-auto divide-y border rounded-lg">
                        {chainList.length === 0 && (
                            <p className="text-sm text-slate-400 text-center py-6">
                                {uncalibrated.length === 0 ? 'Tous les fournisseurs sont calibrés 🎉' : 'Aucun fournisseur trouvé'}
                            </p>
                        )}
                        {chainList.map(s => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => pickChainSupplier(s)}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-orange-50 text-left"
                                data-testid={`chain-pick-${s.id}`}
                            >
                                <span className="font-medium text-slate-700">{s.name}</span>
                                <span className="text-[11px] text-orange-500 font-semibold">Calibrer →</span>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
